/**
 * RightSidebar Component
 * 
 * Displays files for current context (folder or conversation).
 * Features filter tabs, image thumbnails, and file cards.
 */

import React, { useState, useEffect, useRef } from 'react';

export default function RightSidebar({ conversationId, folderId, refreshTrigger, folderName, conversationTitle, isOpen, onClose }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const fileInputRef = useRef(null);

  // Fetch files when context changes
  useEffect(() => {
    if (folderId !== null || conversationId) {
      fetchFiles();
    } else {
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, folderId, refreshTrigger]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      let url = '/api/files?';
      if (folderId !== null) {
        url += `folder_id=${folderId}`;
      } else if (conversationId) {
        url += `conversation_id=${conversationId}`;
      } else {
        setFiles([]);
        return;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      } else {
        console.error('Failed to fetch files:', response.status);
        setFiles([]);
      }
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    
    // Limit to 5 files
    const filesToUpload = selectedFiles.slice(0, 5);
    
    const formData = new FormData();
    filesToUpload.forEach(file => {
      formData.append('files', file);
    });
    
    if (folderId !== null) {
      formData.append('folder_id', String(folderId));
    } else if (conversationId) {
      formData.append('conversation_id', conversationId);
    } else {
      return;
    }

    try {
      const response = await fetch('/api/files/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        await fetchFiles();
      } else {
        const error = await response.json();
        alert(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed');
    }
    
    // Clear file input
    e.target.value = '';
  };

  const handleDelete = async (fileId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this file?')) return;

    try {
      const response = await fetch(`/api/files/${fileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok || response.status === 204) {
        await fetchFiles();
      } else {
        console.error('Delete failed:', response.status);
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleFileClick = (file) => {
    window.open(file.url, '_blank');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const getFileIcon = (mimeType) => {
    if (mimeType && mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📕';
    return '📄';
  };

  const hasContext = folderId !== null || conversationId;

  // Filter files based on active filter
  const filteredFiles = files.filter(file => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'images') return file.mime_type && file.mime_type.startsWith('image/');
    if (activeFilter === 'docs') return !file.mime_type || !file.mime_type.startsWith('image/');
    return true;
  });

  return (
    <div className={`chat-right-sidebar ${isOpen ? "open" : ""}`}>
      {/* Context Bar */}
      <div className="sidebar-context-bar">
        {folderName ? (
          <>
            <span className="context-icon">📁</span>
            <span className="context-label">{folderName}</span>
          </>
        ) : conversationTitle ? (
          <>
            <span className="context-icon">💬</span>
            <span className="context-label">{conversationTitle}</span>
          </>
        ) : (
          <span className="context-label">Files</span>
        )}
        <span className="context-count">{files.length} file{files.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Filter Tabs */}
      <div className="sidebar-filter-tabs">
        {['all', 'images', 'docs'].map(tab => (
          <button
            key={tab}
            className={`filter-tab ${activeFilter === tab ? 'active' : ''}`}
            onClick={() => setActiveFilter(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* File List */}
      {!hasContext ? (
        <div className="sidebar-empty">
          Select a conversation to see files
        </div>
      ) : loading ? (
        <div className="sidebar-empty">Loading...</div>
      ) : filteredFiles.length === 0 ? (
        <div className="sidebar-empty">
          {files.length === 0 
            ? `No files yet. Upload files to attach them to this ${folderId !== null ? 'folder' : 'conversation'}.`
            : 'No files match this filter.'
          }
        </div>
      ) : (
        <div className="sidebar-file-list">
          {filteredFiles.map(file => {
            const isImage = file.mime_type && file.mime_type.startsWith('image/');
            
            if (isImage) {
              return (
                <div 
                  key={file.file_id} 
                  className="file-card file-card-image"
                  onClick={() => handleFileClick(file)}
                >
                  <div className="file-thumbnail">
                    <img 
                      src={file.url} 
                      alt={file.original_filename}
                      className="file-thumbnail-img"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="file-thumbnail-fallback" style={{ display: 'none' }}>
                      {getFileIcon(file.mime_type)}
                    </div>
                    <span className="file-size-badge">{formatFileSize(file.size_bytes)}</span>
                  </div>
                  <div className="file-card-footer">
                    <div className="file-card-info">
                      <div className="file-card-name" title={file.original_filename}>{file.original_filename}</div>
                      <div className="file-card-meta">{formatDate(file.uploaded_at)}</div>
                    </div>
                    <button 
                      className="file-delete-btn"
                      onClick={(e) => handleDelete(file.file_id, e)}
                      title="Delete file"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            } else {
              return (
                <div 
                  key={file.file_id} 
                  className="file-card file-card-doc"
                  onClick={() => handleFileClick(file)}
                >
                  <span className="file-doc-icon">{getFileIcon(file.mime_type)}</span>
                  <div className="file-card-info">
                    <div className="file-card-name" title={file.original_filename}>{file.original_filename}</div>
                    <div className="file-card-meta">{formatFileSize(file.size_bytes)} · {formatDate(file.uploaded_at)}</div>
                  </div>
                  <button 
                    className="file-delete-btn"
                    onClick={(e) => handleDelete(file.file_id, e)}
                    title="Delete file"
                  >
                    ×
                  </button>
                </div>
              );
            }
          })}
        </div>
      )}

      {/* Upload Area */}
      {hasContext && (
        <div className="sidebar-upload-area">
          <button 
            className="sidebar-upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            📎 Upload file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            onChange={handleUpload}
          />
        </div>
      )}
    </div>
  );
}
